import React from 'react';
import type { Feature } from '../../types.ts';
import { RAW_FEATURES } from './manifest.ts';
import { lazyWithRetry } from '../../services/componentLoader.ts';

// Map feature IDs to their components using lazy loading with retry logic
const componentMap: Record<string, React.FC<any>> = {
    'python-repl': lazyWithRetry(() => import('./PythonRepl.tsx'), 'PythonRepl'),
    // Initial
    'ai-code-explainer': lazyWithRetry(() => import('../AiCodeExplainer.tsx'), 'AiCodeExplainer'),
    'ai-feature-builder': lazyWithRetry(() => import('./AiFeatureBuilder.tsx'), 'AiFeatureBuilder'),
    'regex-sandbox': lazyWithRetry(() => import('./RegexSandbox.tsx'), 'RegexSandbox'),
    'api-tester': lazyWithRetry(() => import('./ApiTester.tsx'), 'ApiTester'),
    'portable-snippet-vault': lazyWithRetry(() => import('./SnippetVault.tsx'), 'SnippetVault'),
    'css-grid-editor': lazyWithRetry(() => import('./CssGridEditor.tsx'), 'CssGridEditor'),
    'ai-commit-generator': lazyWithRetry(() => import('../AiCommitGenerator.tsx'), 'AiCommitGenerator'),
    'json-tree-navigator': lazyWithRetry(() => import('./JsonTreeNavigator.tsx'), 'JsonTreeNavigator'),
    'ai-unit-test-generator': lazyWithRetry(() => import('./AiUnitTestGenerator.tsx'), 'AiUnitTestGenerator'),
    'prompt-craft-pad': lazyWithRetry(() => import('./PromptCraftPad.tsx'), 'PromptCraftPad'),
    'linter-formatter': lazyWithRetry(() => import('./CodeFormatter.tsx'), 'CodeFormatter'),
    'env-manager': lazyWithRetry(() => import('./EnvManager.tsx'), 'EnvManager'),
    'schema-designer': lazyWithRetry(() => import('./SchemaDesigner.tsx'), 'SchemaDesigner'),
    'pwa-manifest-editor': lazyWithRetry(() => import('./PwaManifestEditor.tsx'), 'PwaManifestEditor'),
    'clipboard-history': lazyWithRetry(() => import('./ClipboardHistory.tsx'), 'ClipboardHistory'),
    'markdown-slides-generator': lazyWithRetry(() => import('./MarkdownSlides.tsx'), 'MarkdownSlides'),
    'responsive-breakpoint-tester': lazyWithRetry(() => import('./ResponsiveTester.tsx'), 'ResponsiveTester'),
    'meta-tag-editor': lazyWithRetry(() => import('./MetaTagEditor.tsx'), 'MetaTagEditor'),
    'image-optimizer': lazyWithRetry(() => import('./ImageOptimizer.tsx'), 'ImageOptimizer'),
    'color-palette-generator': lazyWithRetry(() => import('./ColorPaletteGenerator.tsx'), 'ColorPaletteGenerator'),

    // Batch 2
    'network-visualizer': lazyWithRetry(() => import('./NetworkVisualizer.tsx'), 'NetworkVisualizer'),
    'audio-to-code': lazyWithRetry(() => import('./AudioToCode.tsx'), 'AudioToCode'),
    'screenshot-to-component': lazyWithRetry(() => import('./ScreenshotToComponent.tsx'), 'ScreenshotToComponent'),
    'feature-flag-board': lazyWithRetry(() => import('./FeatureFlagBoard.tsx'), 'FeatureFlagBoard'),
    'project-moodboard': lazyWithRetry(() => import('./ProjectMoodboard.tsx'), 'ProjectMoodboard'),
    'graphql-runner': lazyWithRetry(() => import('./GraphqlRunner.tsx'), 'GraphqlRunner'),
    'env-diff-tool': lazyWithRetry(() => import('./EnvDiffTool.tsx'), 'EnvDiffTool'),
    'code-audio-narrator': lazyWithRetry(() => import('./CodeAudioNarrator.tsx'), 'CodeAudioNarrator'),
    'custom-cli-generator': lazyWithRetry(() => import('./CustomCliGenerator.tsx'), 'CustomCliGenerator'),
    'static-site-generator-ui': lazyWithRetry(() => import('./StaticSiteGeneratorUI.tsx'), 'StaticSiteGeneratorUI'),
    
    // Final Batch
    'terminal': lazyWithRetry(() => import('./TerminalFeature.tsx'), 'TerminalFeature'),
    'command-palette': lazyWithRetry(() => import('./CommandPaletteTrigger.tsx'), 'CommandPaletteTrigger'),
    'secrets-vault': lazyWithRetry(() => import('./SecretsVault.tsx'), 'SecretsVault'),
    'custom-hook-builder': lazyWithRetry(() => import('./CustomHookBuilder.tsx'), 'CustomHookBuilder'),
    'dev-notes-sticky-panel': lazyWithRetry(() => import('./DevNotesStickyPanel.tsx'), 'DevNotesStickyPanel'),
    'font-preview-picker': lazyWithRetry(() => import('./FontPreviewPicker.tsx'), 'FontPreviewPicker'),
    'dev-mood-tracker': lazyWithRetry(() => import('./DevMoodTracker.tsx'), 'DevMoodTracker'),
    'vim-mode-toggle': lazyWithRetry(() => import('./VimModeToggle.tsx'), 'VimModeToggle'),
    'theme-designer': lazyWithRetry(() => import('./ThemeDesigner.tsx'), 'ThemeDesigner'),
    'dev-soundboard': lazyWithRetry(() => import('./DevSoundboard.tsx'), 'DevSoundboard'),
    'focus-mode': lazyWithRetry(() => import('./FocusMode.tsx'), 'FocusMode'),
    'scratchpad-terminal': lazyWithRetry(() => import('./ScratchpadTerminal.tsx'), 'ScratchpadTerminal'),
    'personal-wiki': lazyWithRetry(() => import('./PersonalWiki.tsx'), 'PersonalWiki'),
    'svg-path-editor': lazyWithRetry(() => import('./SvgPathEditor.tsx'), 'SvgPathEditor'),
    'ai-style-transfer': lazyWithRetry(() => import('./AiStyleTransfer.tsx'), 'AiStyleTransfer'),
    'workspace-teleporter': lazyWithRetry(() => import('./WorkspaceTeleporter.tsx'), 'WorkspaceTeleporter'),
    'ai-coding-challenge': lazyWithRetry(() => import('../AiCodingChallenge.tsx'), 'AiCodingChallenge'),
    'clipboard-to-code': lazyWithRetry(() => import('./ClipboardToCode.tsx'), 'ClipboardToCode'),
    'sleep-mode': lazyWithRetry(() => import('./SleepMode.tsx'), 'SleepMode'),
    'font-pairing-tool': lazyWithRetry(() => import('./FontPairingTool.tsx'), 'FontPairingTool'),
    'tab-sessions-manager': lazyWithRetry(() => import('./TabSessionsManager.tsx'), 'TabSessionsManager'),
    'clipboard-snippet-inserter': lazyWithRetry(() => import('./ClipboardSnippetInserter.tsx'), 'ClipboardSnippetInserter'),
    'code-review-bot': lazyWithRetry(() => import('./CodeReviewBot.tsx'), 'CodeReviewBot'),
    'pr-summary-generator': lazyWithRetry(() => import('./PrSummaryGenerator.tsx'), 'PrSummaryGenerator'),
    'changelog-generator': lazyWithRetry(() => import('./ChangelogGenerator.tsx'), 'ChangelogGenerator'),
    'cron-job-builder': lazyWithRetry(() => import('./CronJobBuilder.tsx'), 'CronJobBuilder'),
    'code-to-flowchart': lazyWithRetry(() => import('./CodeToFlowchart.tsx'), 'CodeToFlowchart'),
    'offline-backup-creator': lazyWithRetry(() => import('./OfflineBackupCreator.tsx'), 'OfflineBackupCreator'),
    'content-manager': lazyWithRetry(() => import('./ContentManager.tsx'), 'ContentManager'),
};

// Map feature IDs to specific AI configurations
const aiConfigMap: Record<string, Feature['aiConfig']> = {
    'python-repl': { model: 'gemini-2.5-flash' },
    'ai-code-explainer': { model: 'gemini-2.5-flash' },
    'ai-feature-builder': { model: 'gemini-2.5-flash' },
    'ai-commit-generator': { model: 'gemini-2.5-flash' },
    'ai-unit-test-generator': { model: 'gemini-2.5-flash' },
    'regex-sandbox': { model: 'gemini-2.5-flash' },
    'screenshot-to-component': { model: 'gemini-2.5-flash' },
    'audio-to-code': { model: 'gemini-2.5-flash' },
    'color-palette-generator': { model: 'gemini-2.5-flash' },
    'ai-style-transfer': { model: 'gemini-2.5-flash' },
    'ai-coding-challenge': { model: 'gemini-2.5-flash' },
    'code-review-bot': { model: 'gemini-2.5-flash' },
    'pr-summary-generator': { model: 'gemini-2.5-flash' },
    'code-to-flowchart': { model: 'gemini-2.5-flash' },
};

// "Compile" the raw data into the final Feature objects
export const ALL_FEATURES: Feature[] = RAW_FEATURES.map(feature => ({
    ...feature,
    component: componentMap[feature.id],
    aiConfig: aiConfigMap[feature.id] || { model: 'gemini-2.5-flash' },
}));

// Create a simple map for quick lookups
export const FEATURES_MAP = new Map(ALL_FEATURES.map(f => [f.id, f]));